/**
 * Bounded stack utilities for undo/redo stacks.
 *
 * Caps the stack at a fixed maximum size to prevent unbounded memory growth
 * and bound the cost of push/pop operations to O(MAX_SIZE) instead of O(n).
 *
 * The stacks remain plain arrays for Zustand compatibility and simple serialization.
 */

/**
 * Maximum number of entries in the undo or redo stack.
 * When a push would exceed this limit, the oldest entry is dropped.
 */
export const MAX_UNDO_REDO_STACK_SIZE = 500;

/**
 * Pushes an item onto a bounded stack, dropping the oldest entry if at capacity.
 * Returns a new array (immutable — safe for Zustand state updates).
 *
 * @param stack  The current stack (readonly, not mutated).
 * @param item   The item to push.
 * @param maxSize Maximum stack size (defaults to MAX_UNDO_REDO_STACK_SIZE).
 * @returns A new array with the item appended, at most maxSize entries.
 */
export function boundedStackPush<T>(stack: readonly T[], item: T, maxSize: number = MAX_UNDO_REDO_STACK_SIZE): T[] {
    if (stack.length < maxSize) {
        return [...stack, item];
    }
    // At capacity: drop the oldest entry (index 0), append new item.
    // Pre-allocate the result array for a single O(maxSize) pass.
    const result = new Array<T>(maxSize);
    for (let i = 1; i < maxSize; i++) {
        result[i - 1] = stack[i]!;
    }
    result[maxSize - 1] = item;
    return result;
}
